import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { useRealtimeEvent } from "@/lib/realtime/RealtimeProvider";
import { listDrafts } from "../../../api/drafts-v2";
import { getPacketByIndividual } from "../../../api/packets-v2";
import type { Draft } from "../../../api/types/draft";
import type { Packet } from "../../../api/types/packet";
import { CasePrepUscisFormsAPI } from "../../casePrepUscisForms/api";
import { fillOutcome } from "../../casePrepUscisForms/fillProgress";
import type {
  CasePrepUscisFilledForm,
  CasePrepUscisFormStatus,
  CasePrepUscisRunProgress
} from "../../casePrepUscisForms/types";
import { CaseAgentAPI } from "../chat/api/caseAgentApi";
import { useOnInvalidate } from "../context/CaseIdeContext";
import type { CaseAgentPlan, InvalidateTarget } from "../types";
import { useWorkspaceTree, type WorkspaceTree } from "./useWorkspaceTree";
import {
  anyRunning,
  buildSections,
  type WorkspaceSection,
  type WorkspaceSources
} from "./workspaceModel";

const ACTIVE_POLL_MS = 4000;

type FormProgressEvent = {
  individual_id: string;
  filled_form_id: string;
  status?: CasePrepUscisFormStatus | null;
  progress?: CasePrepUscisRunProgress | null;
  last_run_error?: string | null;
};

type Source = "drafts" | "packet" | "forms" | "plans";

const ALL_SOURCES: Source[] = ["drafts", "packet", "forms", "plans"];

const SOURCE_FOR_TARGET: Partial<Record<InvalidateTarget, Source[]>> = {
  drafts: ["drafts"],
  research: ["drafts"],
  packet: ["packet"],
  forms: ["forms"],
  plans: ["plans"],
  // Filesystem edits change the exhibit list and the packet freshness.
  fs: ["drafts", "packet"],
  autocategorize: ["drafts", "packet"]
};

export type CaseWorkspace = {
  /** Items grouped by kind: the index the chat, mentions and tool cards use. */
  sections: WorkspaceSection[];
  /** The artifacts themselves, for joining tree nodes to titles and statuses. */
  sources: WorkspaceSources;
  drafts: Draft[];
  packet: Packet | null;
  forms: CasePrepUscisFilledForm[];
  /** Plans the attorney saved to the workspace. */
  plans: CaseAgentPlan[];
  /** The attorney's folder structure over those artifacts. */
  tree: WorkspaceTree;
  isLoading: boolean;
  error: string | null;
  /** Every list has loaded, forms included (they wait on the case id). */
  complete: boolean;
  refetch: (sources?: Source[]) => Promise<void>;
};

/**
 * Everything the case produces, composed from the drafts, packet and forms
 * APIs, plus the workspace tree that organises them. Refetches on
 * `data-invalidate` and polls while anything is running.
 */
export const useCaseWorkspace = (
  individualId: string,
  caseId: string | null
): CaseWorkspace => {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [packet, setPacket] = useState<Packet | null>(null);
  const [forms, setForms] = useState<CasePrepUscisFilledForm[]>([]);
  const [plans, setPlans] = useState<CaseAgentPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [loadedFor, setLoadedFor] = useState(individualId);
  if (loadedFor !== individualId) {
    setLoadedFor(individualId);
    setDrafts([]);
    setPacket(null);
    setForms([]);
    setPlans([]);
    setError(null);
    setIsLoading(true);
  }
  // A list that lands after a case switch belongs to the old case.
  const currentCase = useRef(individualId);
  useEffect(() => {
    currentCase.current = individualId;
  }, [individualId]);
  // A fetch already on its way is joined, not skipped: a refetch that returned
  // early would end loading with empty lists and prune the open tabs.
  const inflight = useRef<Map<string, Promise<void>>>(new Map());
  const tree = useWorkspaceTree(individualId);
  const refreshTree = tree.refresh;

  const loadSource = useCallback(
    async (source: Source) => {
      const current = () => currentCase.current === individualId;
      if (source === "drafts") {
        const next = await listDrafts({ individual_id: individualId });
        if (current()) setDrafts(next);
      } else if (source === "packet") {
        try {
          const next = await getPacketByIndividual(individualId);
          if (current()) setPacket(next);
        } catch (e) {
          // A case without a packet yet is a 404, not an error worth surfacing.
          if ((e as { status?: number }).status === 404) {
            if (current()) setPacket(null);
          } else throw e;
        }
      } else if (source === "plans") {
        const next = await CaseAgentAPI.listWorkspacePlans(individualId);
        if (current()) setPlans(next);
      } else if (caseId) {
        const next = await CasePrepUscisFormsAPI.listFilledForms(
          individualId,
          caseId
        );
        if (current()) setForms(next);
      }
    },
    [caseId, individualId]
  );

  const fetchSource = useCallback(
    (source: Source): Promise<void> => {
      const key = `${source}:${individualId}:${caseId ?? ""}`;
      const pending = inflight.current.get(key);
      if (pending) return pending;
      const run = loadSource(source).finally(() => {
        inflight.current.delete(key);
      });
      inflight.current.set(key, run);
      return run;
    },
    [caseId, individualId, loadSource]
  );

  const refetch = useCallback(
    async (sources: Source[] = ALL_SOURCES) => {
      // The tree is reconciled server-side against the artifacts, so a change
      // to any of them can add or drop a node; fetch both in one pass.
      const results = await Promise.allSettled([
        ...[...new Set(sources)].map(fetchSource),
        refreshTree()
      ]);
      const failed = results.find(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      setError(
        failed
          ? (failed.reason as Error)?.message ?? "Failed to load workspace"
          : null
      );
    },
    [fetchSource, refreshTree]
  );

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    refetch().finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [refetch]);

  useOnInvalidate(
    ["drafts", "research", "packet", "forms", "plans", "fs", "autocategorize"],
    useCallback(
      (targets: InvalidateTarget[]) => {
        const sources = targets.flatMap((t) => SOURCE_FOR_TARGET[t] ?? []);
        void refetch(sources.length ? sources : undefined);
      },
      [refetch]
    )
  );

  useRealtimeEvent<FormProgressEvent>("form.progress", (data) => {
    if (data.individual_id !== individualId) return;
    if (data.status !== "generating") {
      // Finished: the answers changed too, so read the form back whole.
      void fetchSource("forms");
      return;
    }
    setForms((prev) =>
      prev.map((f) =>
        f.case_extend_uscis_filled_form_id === data.filled_form_id
          ? {
              ...f,
              status: "generating",
              run_progress: data.progress ?? null,
              run_stalled: false
            }
          : f
      )
    );
  });

  // One toast per finished fill, whether the stream or the poll saw it end.
  const previousForms = useRef<Map<string, CasePrepUscisFilledForm>>(new Map());
  useEffect(() => {
    const before = previousForms.current;
    for (const form of forms) {
      const outcome = fillOutcome(
        before.get(form.case_extend_uscis_filled_form_id),
        form
      );
      if (outcome) {
        toast[outcome.tone](outcome.title, {
          description: outcome.description
        });
      }
    }
    previousForms.current = new Map(
      forms.map((f) => [f.case_extend_uscis_filled_form_id, f])
    );
  }, [forms]);

  const sources = useMemo<WorkspaceSources>(
    () => ({ drafts, packet, forms, plans }),
    [drafts, packet, forms, plans]
  );
  const sections = useMemo(() => buildSections(sources), [sources]);

  // Poll only while something is generating; the invalidate bus covers the rest.
  // Structure does not change while a draft generates, so poll the artifacts alone.
  // A building plan is not polled: the agent invalidates `plans` as it checks off todos.
  const running = anyRunning(sections.filter((s) => s.id !== "plans"));
  useEffect(() => {
    if (!running) return undefined;
    const poll = () => {
      void Promise.allSettled(ALL_SOURCES.map(fetchSource));
    };
    const id = window.setInterval(poll, ACTIVE_POLL_MS);
    return () => window.clearInterval(id);
  }, [running, fetchSource]);

  return {
    sections,
    sources,
    drafts,
    packet,
    forms,
    plans,
    tree,
    isLoading: isLoading || tree.isLoading,
    error: error ?? tree.error,
    complete:
      !isLoading && !tree.isLoading && !error && !tree.error && !!caseId,
    refetch
  };
};
